// Chat History Module
// Browse and read past conversation sessions via REST API

import { CONFIG } from '../config.js';
import { getToken, isUserSignedIn, handleAuthError } from './auth.js';

const API_BASE = CONFIG.AUTH_API_BASE;

let currentSessionsPage = 1;
let currentMessagesPage = 1;
let hasMoreSessions = false;
let hasMoreMessages = false;
let currentSessionId = null;

/**
 * Fetch chat sessions list from API
 */
async function loadSessions(page = 1) {
    const token = getToken();
    if (!token) return null;

    try {
        const response = await fetch(`${API_BASE}/api/child/chat/sessions?page=${page}&limit=20`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const authResult = await handleAuthError(response);
            if (authResult.handled) return null;

            if (response.status === 403) {
                if (window.showToast) {
                    window.showToast(window.i18n?.t('chatHistory.accessDenied') || 'Access denied', 'error');
                }
                return null;
            }
            throw new Error(`HTTP ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error loading chat sessions:', error);
        if (window.showToast) {
            window.showToast(window.i18n?.t('chatHistory.loadError') || 'Failed to load chat history', 'error');
        }
        return null;
    }
}

/**
 * Fetch messages for a specific session
 */
async function loadMessages(sessionId, page = 1) {
    const token = getToken();
    if (!token) return null;

    try {
        const response = await fetch(`${API_BASE}/api/child/chat/sessions/${sessionId}/messages?page=${page}&limit=50`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const authResult = await handleAuthError(response);
            if (authResult.handled) return null;

            if (response.status === 403) {
                if (window.showToast) {
                    window.showToast(window.i18n?.t('chatHistory.accessDenied') || 'Access denied', 'error');
                }
                return null;
            }
            throw new Error(`HTTP ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error loading messages:', error);
        if (window.showToast) {
            window.showToast(window.i18n?.t('chatHistory.loadError') || 'Failed to load messages', 'error');
        }
        return null;
    }
}

/**
 * Format a date string for display
 */
function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return dateStr;
    }
}

/**
 * Open the chat history modal and show sessions list
 */
export async function openChatHistory() {
    if (!isUserSignedIn()) return;

    const modal = document.getElementById('chatHistoryModal');
    if (!modal) return;

    modal.classList.add('show');

    // Show sessions view, hide messages view
    showSessionsView();

    // Reset and load first page
    currentSessionsPage = 1;
    const sessionsList = document.getElementById('chatHistorySessionsList');
    if (sessionsList) sessionsList.innerHTML = '';

    await loadMoreSessions();
}

/**
 * Close the chat history modal
 */
export function closeChatHistory() {
    const modal = document.getElementById('chatHistoryModal');
    if (modal) modal.classList.remove('show');
}

/**
 * Switch to sessions list view
 */
function showSessionsView() {
    const sessionsView = document.getElementById('chatHistorySessionsView');
    const messagesView = document.getElementById('chatHistoryMessagesView');
    if (sessionsView) sessionsView.style.display = 'block';
    if (messagesView) messagesView.style.display = 'none';
}

/**
 * Switch to messages view for a specific session
 */
function showMessagesView() {
    const sessionsView = document.getElementById('chatHistorySessionsView');
    const messagesView = document.getElementById('chatHistoryMessagesView');
    if (sessionsView) sessionsView.style.display = 'none';
    if (messagesView) messagesView.style.display = 'block';
}

/**
 * Load more sessions (pagination)
 */
async function loadMoreSessions() {
    const sessionsList = document.getElementById('chatHistorySessionsList');
    const loadMoreBtn = document.getElementById('chatHistoryLoadMoreSessions');
    const emptyState = document.getElementById('chatHistoryEmpty');
    const loading = document.getElementById('chatHistoryLoading');

    if (loading) loading.style.display = 'block';
    if (loadMoreBtn) loadMoreBtn.style.display = 'none';
    if (emptyState) emptyState.style.display = 'none';

    const data = await loadSessions(currentSessionsPage);

    if (loading) loading.style.display = 'none';

    if (!data) return;

    const sessions = data.sessions || data.items || data;
    const isArray = Array.isArray(sessions);

    if (!isArray || sessions.length === 0) {
        if (currentSessionsPage === 1) {
            if (emptyState) emptyState.style.display = 'block';
        }
        return;
    }

    sessions.forEach(session => {
        const card = document.createElement('div');
        card.className = 'chat-history-session-card';
        card.onclick = () => openSession(session.id || session.session_id, session.title || session.name || '');

        const title = session.title || session.name || (window.i18n?.t('chatHistory.untitledSession') || 'Conversation');
        const date = formatDate(session.created_at || session.updated_at || session.date);
        const messageCount = session.message_count ?? session.messages_count ?? '';

        card.innerHTML = `
            <div class="chat-history-session-title">${escapeHtml(title)}</div>
            <div class="chat-history-session-meta">
                <span class="chat-history-session-date">${date}</span>
                ${messageCount !== '' ? `<span class="chat-history-session-count">${messageCount} ${window.i18n?.t('chatHistory.messages') || 'messages'}</span>` : ''}
            </div>
        `;
        sessionsList.appendChild(card);
    });

    // Check for more pages
    hasMoreSessions = sessions.length >= 20;
    if (data.total && sessionsList.children.length >= data.total) {
        hasMoreSessions = false;
    }
    if (loadMoreBtn) {
        loadMoreBtn.style.display = hasMoreSessions ? 'block' : 'none';
    }
    currentSessionsPage++;
}

/**
 * Open a specific session and show its messages
 */
async function openSession(sessionId, title) {
    currentSessionId = sessionId;
    currentMessagesPage = 1;

    showMessagesView();

    // Set session title in header
    const titleEl = document.getElementById('chatHistorySessionTitle');
    if (titleEl) {
        titleEl.textContent = title || (window.i18n?.t('chatHistory.untitledSession') || 'Conversation');
    }

    // Clear messages
    const messagesList = document.getElementById('chatHistoryMessagesList');
    if (messagesList) messagesList.innerHTML = '';

    await loadMoreMessages();
}

/**
 * Load more messages for current session (pagination)
 */
async function loadMoreMessages() {
    if (!currentSessionId) return;

    const messagesList = document.getElementById('chatHistoryMessagesList');
    const loadMoreBtn = document.getElementById('chatHistoryLoadMoreMessages');
    const loading = document.getElementById('chatHistoryMessagesLoading');

    if (loading) loading.style.display = 'block';
    if (loadMoreBtn) loadMoreBtn.style.display = 'none';

    const data = await loadMessages(currentSessionId, currentMessagesPage);

    if (loading) loading.style.display = 'none';

    if (!data) return;

    const messages = data.messages || data.items || data;
    const isArray = Array.isArray(messages);

    if (!isArray || messages.length === 0) return;

    // For page > 1, older messages go on top
    const fragment = document.createDocumentFragment();

    messages.forEach(msg => {
        const bubble = document.createElement('div');
        const role = msg.role || msg.type || 'user';
        const isUser = role === 'user' || role === 'human';

        bubble.className = `chat-message chat-history-bubble ${isUser ? 'user-message' : 'bot-message'}`;
        bubble.innerHTML = escapeHtml(msg.content || msg.text || '');

        if (currentMessagesPage > 1) {
            fragment.appendChild(bubble);
        } else {
            messagesList.appendChild(bubble);
        }
    });

    if (currentMessagesPage > 1 && messagesList.firstChild) {
        messagesList.insertBefore(fragment, messagesList.firstChild);
    } else if (currentMessagesPage > 1) {
        messagesList.appendChild(fragment);
    }

    // Check for more pages
    hasMoreMessages = messages.length >= 50;
    if (data.total && messagesList.children.length >= data.total) {
        hasMoreMessages = false;
    }
    if (loadMoreBtn) {
        loadMoreBtn.style.display = hasMoreMessages ? 'block' : 'none';
    }
    currentMessagesPage++;

    // Scroll to bottom on first load
    if (currentMessagesPage === 2) {
        messagesList.scrollTop = messagesList.scrollHeight;
    }
}

/**
 * Go back from messages to sessions list
 */
function goBackToSessions() {
    currentSessionId = null;
    showSessionsView();
}

/**
 * Simple HTML escape
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Initialize chat history module - bind event listeners
 */
export function initChatHistory() {
    // Close button
    const closeBtn = document.getElementById('closeChatHistoryModal');
    if (closeBtn) {
        closeBtn.addEventListener('click', closeChatHistory);
    }

    // Back button
    const backBtn = document.getElementById('chatHistoryBackBtn');
    if (backBtn) {
        backBtn.addEventListener('click', goBackToSessions);
    }

    // Load more sessions
    const loadMoreSessionsBtn = document.getElementById('chatHistoryLoadMoreSessions');
    if (loadMoreSessionsBtn) {
        loadMoreSessionsBtn.addEventListener('click', loadMoreSessions);
    }

    // Load more messages
    const loadMoreMessagesBtn = document.getElementById('chatHistoryLoadMoreMessages');
    if (loadMoreMessagesBtn) {
        loadMoreMessagesBtn.addEventListener('click', loadMoreMessages);
    }

    // History button in chat modal
    const historyBtn = document.getElementById('chatHistoryBtn');
    if (historyBtn) {
        historyBtn.addEventListener('click', openChatHistory);
    }

    // Close on overlay click
    const modal = document.getElementById('chatHistoryModal');
    if (modal) {
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeChatHistory();
        });
    }
}
